<template>
    <div>
        <div class="gallery">
            <!-- Snap left button -->
            <div v-show="scrollLeft > 0" @click="snapLeft">
                <div class="left-button">
                    <UIIcon :name="['far', 'chevron-left']" />
                </div>
            </div>
            <div ref="gallery" class="thumbnails" :class="{ scroll: !largeVariant }">
                <div
                    v-for="(thumbnail, index) in thumbnails"
                    :key="thumbnail"
                    class="img-wrapper"
                    @click="activateLightbox(index)"
                >
                    <UIImage
                        :resource="resource"
                        :width="200"
                        :raw-src="false"
                        :src="thumbnail"
                        class="img"
                        :responsive-widths="[768, 480]"
                        sizes="(max-width: 960px) 200px, 340px"
                    />
                    <UIIcon :name="['far', 'search-plus']" />
                </div>
            </div>
            <!-- Snap right button -->
            <div v-show="largeImages.length > 3 && scrollLeft < (largeImages.length - 2) * snapStep" @click="snapRight">
                <div class="right-button">
                    <UIIcon :name="['far', 'chevron-right']" />
                </div>
            </div>
        </div>

        <!-- Lightbox starts here -->
        <div class="light-box">
            <transition name="fade" mode="out-in" tag="div">
                <div v-if="lightboxActive" class="bg" @click.stop="close" />
            </transition>

            <div v-if="lightboxActive">
                <!-- close-button button -->
                <div class="close-button" @click="close">
                    <UIIcon :name="['fal', 'times']" />
                </div>

                <p v-if="count" class="count">
                    {{ currentImage + 1 }}/<span>{{ thumbnails.length }}</span>
                </p>

                <!-- Previous button -->
                <div v-if="buttons" class="btn prev" @click="prev">
                    <UIIcon :name="['far', 'chevron-left']" class="prev" />
                </div>

                <div class="container">
                    <!-- Mobile swiper for lightbox image -->
                    <UISwiper
                        v-if="lightboxActive"
                        :active="enableSwipe"
                        class="swiper"
                        @swipeLeft="onSwipeLeft"
                        @swipeRight="onSwipeRight"
                    >
                        <transition name="fade" mode="out-in" tag="div">
                            <UIImage
                                :key="currentImage"
                                :resource="resource"
                                :raw-src="currentImage === 0 && coverImage ? true : false"
                                :src="largeImages[currentImage]"
                            />
                        </transition>
                    </UISwiper>
                </div>

                <!-- Forward button -->
                <div v-if="buttons" class="btn next" @click="next">
                    <UIIcon :name="['far', 'chevron-right']" class="next" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { UISwiper, UIIcon, UIImage } from '@dundle/ui/components';

export default {
    components: {
        UISwiper,
        UIIcon,
        UIImage,
    },

    props: {
        thumbnails: {
            type: Array,
            required: true,
        },
        largeImages: {
            type: Array,
            required: true,
        },
        coverImage: {
            type: String,
            required: false,
        },
        // fallbackCover: {
        //     type: String,
        //     required: false
        // },
        count: {
            type: Boolean,
            required: false,
            default: true,
        },
        buttons: {
            type: Boolean,
            required: false,
            default: true,
        },
        largeVariant: {
            type: Boolean,
            required: false,
            default: true,
        },
        resource: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            lightboxActive: false,
            currentImage: 0,
            enableSwipe: false,
            scrollLeft: 0,
            // The index the current image is selected from
            fromIndex: 0,
            // How much is scrolled for one image
            snapStep: 97,
        };
    },

    watch: {
        // If the image at the end or start of the column is clicked, scroll left or right automatically
        currentImage() {
            if (this.currentImage === 3 || ((this.currentImage - 3) % 3 === 0 && this.currentImage != 0)) {
                if (this.currentImage > this.fromIndex) {
                    this.snapRight();
                } else if (this.currentImage < this.fromIndex) {
                    this.snapLeft();
                }
            }
            this.fromIndex = this.currentImage;
        },
    },

    mounted() {
        // Redirect all keypress events to the keyPress method
        document.onkeydown = this.keyPress;
        if (this.$device.isMobile()) {
            this.enableSwipe = true;
        }
    },

    methods: {
        showImage(index) {
            this.currentImage = index;
            if (!this.largeVariant) {
                this.activateLightbox();
            }
        },
        activateLightbox(index) {
            this.currentImage = index;
            this.lightboxActive = true;
        },
        next() {
            if (this.currentImage < this.largeImages.length - 1) {
                this.currentImage++;
            } else {
                this.currentImage = 0;
                this.snapStart();
            }
        },
        prev() {
            if (this.currentImage > 0) {
                this.currentImage--;
            } else {
                this.currentImage = this.largeImages.length - 1;
                this.snapEnd();
            }
        },
        close() {
            if (this.lightboxActive) {
                this.lightboxActive = false;
            }
        },
        keyPress(e) {
            switch (e.keyCode) {
                // Right arrow key
                case 39:
                    if (this.lightboxActive) {
                        this.next();
                    }
                    break;
                // Left arrow key
                case 37:
                    if (this.lightboxActive) {
                        this.prev();
                    }
                    break;
                // Escape button
                case 27:
                    this.close();
                    break;
                default:
                    break;
            }
        },
        onSwipeRight() {
            this.prev();
        },
        onSwipeLeft() {
            this.next();
        },
        snapRight() {
            // Snapright and snapleft scroll by one column (4 images)
            this.$refs.gallery.scrollTo({ left: this.scrollLeft + this.snapStep * 4, behavior: 'smooth' });
            this.scrollLeft += this.snapStep * 4;
        },
        snapLeft() {
            this.$refs.gallery.scrollTo({ left: this.scrollLeft - this.snapStep * 4, behavior: 'smooth' });
            if (this.scrollLeft - this.snapStep * 4 > 0) {
                this.scrollLeft -= this.snapStep * 4;
            } else {
                this.scrollLeft = 0;
            }
        },
        snapStart() {
            this.$refs.gallery.scrollTo({ left: 0, behavior: 'auto' });
            this.scrollLeft = 0;
        },
        snapEnd() {
            if (this.largeImages.length > 3) {
                // Fromindex is set to the last image because otherwise it might scroll right automatically
                this.fromIndex = this.currentImage;
                // If the number of images is uneven the length to the final image will be less
                const uneven = this.largeImages.length % 2;
                const row = this.snapStep * 4;
                // Always snaps to the end of a row so it has the same behaviour as the "snapRight" method
                const scroll = Math.ceil(((this.largeImages.length - uneven) * this.snapStep) / row) * row;
                this.$refs.gallery.scrollTo({ left: scroll, behavior: 'auto' });
                this.scrollLeft = scroll;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
// Mixins
@mixin aftercontent {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
}

// ----

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.1s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.main-img-container {
    position: relative;
}

.main-img-wrapper {
    @media screen and (max-width: 768px) and (min-width: 480px) {
        height: calc(100% + 15px);
    }
}

.first-image {
    &::after {
        @include aftercontent;
        background: rgba($color-primary-light, 0.6) !important;
        cursor: pointer;
        margin-left: 0;
        @media screen and (max-width: 768px) and (min-width: 480px) {
            top: 15px;
        }
        @media screen and (max-width: 480px) {
            margin-left: -15px;
            width: calc(100% + 30px);
        }
    }
}

.main {
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    height: 255px;
    cursor: pointer;
    object-fit: cover;

    @media screen and (max-width: 480px) {
        position: relative;
        width: 100%;
        top: 0;
        margin-left: -15px;
        margin-right: -15px;
        width: calc(100% + 30px);
    }

    @media screen and (max-width: 768px) and (min-width: 480px) {
        top: 1rem;
        position: relative;
        height: 370px;
        margin-top: -2rem;
        width: 100%;
    }
}

.cover {
    position: absolute;
    height: 80%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
    cursor: pointer;
}

.indicator {
    position: absolute;
    bottom: 1.5rem;
    left: 2rem;
    padding-left: 9px;
    padding-right: 9px;
    color: $color-text-inverted;
    font-weight: bold;

    background-color: $color-grey-9;

    @media (max-width: 480px) {
        left: 0;
    }

    @media (max-width: 768px) and (min-width: 480px) {
        bottom: 0.5rem;
    }
}

.gallery {
    position: relative;
    user-select: none;

    .thumbnails {
        display: flex;
        overflow-x: scroll;
        overflow-y: hidden;
        // margin-top: 10px;
        @media (min-width: 768px) {
            overflow-x: hidden;
        }

        &.scroll {
            @media screen and (max-width: 767px) {
                overflow-x: scroll;
            }
        }
        @media screen and (max-width: 767px) {
            border-top: 1px solid $color-grey-3;
            border-bottom: 1px solid $color-grey-3;
            background: $color-grey-1;
            padding: 1rem 0;
            margin: 0 -15px;
        }
        @media screen and (max-width: 768px) and (min-width: 480px) {
            overflow-x: scroll;
            margin-top: 25px;
        }
    }

    .img-wrapper {
        @media (max-width: 767px) {
            &:first-child {
                margin-left: 15px !important;
            }
        }
        margin: 5px 10px 5px 0;
        cursor: pointer;
        position: relative;

        & > svg {
            display: none;
        }
        &:hover {
            &::after {
                @include aftercontent;
                background: rgba(0, 0, 0, 0.5);
            }
            & > svg {
                width: 2rem;
                height: 2rem;
                color: white;
                position: absolute;
                left: calc(50% - 1rem);
                z-index: 1;
                top: calc(50% - 1rem);
                display: block;
            }
        }
        &.selected {
            &::after,
            &::before {
                @include aftercontent;
            }
            &::after {
                border: 2px solid rgba($color-primary, 1);
            }
            &::before {
                border: 3px solid rgba($color-bg, 1);
            }
        }
    }

    .img {
        min-width: 178px;
        height: 100px;
        // height: 100%;
        object-fit: cover;
        &:hover {
            display: block;
        }
    }

    .left-button,
    .right-button {
        position: absolute;
        top: 5px;
        bottom: 5px;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        cursor: pointer;
        user-select: none;
        color: $color-primary;
        & > svg {
            width: 1.5rem;
            height: 1.5rem;
            color: white;
            margin: 0.5rem;
        }
        @media (max-width: 768px) {
            display: none;
        }
    }

    .left-button {
        left: 0;
        z-index: 1;
    }

    .right-button {
        right: 0;
        z-index: 1;
    }
}

.light-box {
    .container {
        position: fixed;
        z-index: 2000;
        display: flex;
        padding: 0;
        justify-content: center;
        align-items: center;
        left: 50%;
        top: 50%;
        max-width: 100%;
        transform: translate(-50%, -50%);

        @media (min-width: 768px) {
            max-width: 50%;
        }

        img {
            user-select: none;
            align-self: center;
            max-width: 100%;
        }
    }

    .bg {
        position: fixed;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(black, 0.918);
        z-index: 1000;
    }

    .close-button {
        position: fixed;
        padding: 0.5rem;
        right: 1rem;
        top: 1rem;
        font-size: 1rem;
        color: $color-bg;
        border-radius: 50%;
        width: 3rem;
        height: 3rem;
        background-color: rgba($color-bg, 0.1);
        & > svg {
            position: relative;
            width: 2rem;
            height: 2rem;
            color: white;
            z-index: 1;
            display: block;
        }
        &:hover {
            background-color: rgba($color-bg, 0.2);
        }
    }

    .btn {
        border-radius: 50%;

        align-items: center;
        justify-content: center;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        background-color: rgba($color-bg, 0.1);
        width: 4rem;
        height: 4rem;
        & > svg {
            width: 2rem;
            height: 2rem;
        }
        &.next {
            right: 2%;
        }
        &.prev {
            left: 2%;
        }
        &:hover {
            background-color: rgba($color-bg, 0.2);
        }
        @media (max-width: 768px) {
            background-color: rgba($color-bg, 0.2);
            height: 44px;
            width: 44px;
            top: 80%;

            &:hover {
                background-color: rgba($color-bg, 0.2);
            }
        }
    }

    .close-button,
    .btn {
        cursor: pointer;
    }

    .close-button,
    .btn,
    .count {
        position: fixed;
        z-index: 5000;
    }

    .next,
    .prev {
        padding: 1px;
        color: $color-text-inverted;
    }

    .next {
        margin-left: 0.3rem;
    }

    .prev {
        margin-right: 0.3rem;
    }

    .count {
        background-color: rgba($color-bg, 0.1);
        padding: 0 0.8rem;
        border-radius: 1rem;
        right: 6rem;
        top: 1.8rem;
        font-size: 1rem;
        color: $color-grey-1;
        margin: 0;
    }
}

.swiper {
    z-index: 1001;
}
</style>
